import React from "react";
import { Button } from "@/components/ui/button";
import { ChevronUp, ChevronDown, GraduationCap, Microscope, Calculator, Brain, Beaker, ShieldCheck, Globe, CloudCog, Network, Code, Building, Award, FileText } from "lucide-react";
import { Topic } from "@/types/learning-path";
import { SubcategoryItem } from "./SubcategoryItem";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";

interface CategoryCardProps {
  category: Topic;
  index: number;
  totalCategories: number;
  status: "completed" | "in-progress" | "locked";
  isExpanded: boolean;
  onToggle: (categoryId: string) => void;
  getTopicStatus: (topic: Topic) => "completed" | "in-progress" | "locked";
  recommendedDifficulty?: 'easy' | 'medium' | 'hard';
  onGenerateLecture?: (topic: string) => void;
  onGenerateInsights?: (topic: string) => void;
}

export const CategoryCard: React.FC<CategoryCardProps> = ({
  category,
  index,
  totalCategories,
  status,
  isExpanded,
  onToggle,
  getTopicStatus,
  recommendedDifficulty,
  onGenerateLecture,
  onGenerateInsights,
}) => {
  const { isMobile, screenSize } = useIsMobile();
  const isSmallScreen = screenSize === 'xs' || screenSize === 'sm';
  
  const getCategoryIcon = (categoryId: string) => {
    switch (categoryId) {
      case "1": return <Calculator className="w-4 h-4 sm:w-5 sm:h-5" />; // JEE Genius
      case "2": return <Microscope className="w-4 h-4 sm:w-5 sm:h-5" />; // NEET Mastery
      case "3": return <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5" />; // GATE Pathfinder
      case "4": return <FileText className="w-4 h-4 sm:w-5 sm:h-5" />; // UPSC Visionary
      case "5": return <Building className="w-4 h-4 sm:w-5 sm:h-5" />; // Govt Exam Wizard
      case "6": return <Brain className="w-4 h-4 sm:w-5 sm:h-5" />; // Data Science Ace
      case "7": return <Code className="w-4 h-4 sm:w-5 sm:h-5" />; // Coding Maestro
      case "8": return <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5" />; // CyberSecure Pro
      case "9": return <CloudCog className="w-4 h-4 sm:w-5 sm:h-5" />; // Cloud Architect
      case "10": return <Network className="w-4 h-4 sm:w-5 sm:h-5" />; // AI & ML Prodigy
      default: return <Award className="w-4 h-4 sm:w-5 sm:h-5" />;
    }
  };
  
  const getCategoryBackgroundColor = (categoryId: string) => {
    switch (categoryId) {
      case "1": return {
        bg: "bg-gradient-to-r from-blue-200 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/20", 
        border: "border-blue-300 dark:border-blue-700", 
        text: "text-blue-800 dark:text-blue-200",
        gradientText: "bg-gradient-to-r from-blue-700 to-blue-500 dark:from-blue-400 dark:to-blue-300 bg-clip-text text-transparent font-semibold"
      }; // JEE Genius - bright blue theme to match dashboard
      case "2": return {
        bg: "bg-gradient-to-r from-green-200 to-green-100 dark:from-green-900/30 dark:to-green-800/20", 
        border: "border-green-300 dark:border-green-700", 
        text: "text-green-800 dark:text-green-200",
        gradientText: "bg-gradient-to-r from-green-700 to-green-500 dark:from-green-400 dark:to-green-300 bg-clip-text text-transparent font-semibold"
      }; // NEET Mastery - enhanced green
      case "3": return {
        bg: "bg-gradient-to-r from-purple-200 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/20", 
        border: "border-purple-300 dark:border-purple-700", 
        text: "text-purple-800 dark:text-purple-200",
        gradientText: "bg-gradient-to-r from-purple-700 to-purple-500 dark:from-purple-400 dark:to-purple-300 bg-clip-text text-transparent font-semibold"
      }; // GATE Pathfinder - enhanced purple
      case "4": return {
        bg: "bg-gradient-to-r from-amber-200 to-amber-100 dark:from-amber-900/30 dark:to-amber-800/20", 
        border: "border-amber-300 dark:border-amber-700", 
        text: "text-amber-800 dark:text-amber-200",
        gradientText: "bg-gradient-to-r from-amber-700 to-amber-500 dark:from-amber-400 dark:to-amber-300 bg-clip-text text-transparent font-semibold"
      }; // UPSC Visionary - enhanced amber
      case "5": return {
        bg: "bg-gradient-to-r from-gray-200 to-gray-100 dark:from-gray-900/30 dark:to-gray-800/20", 
        border: "border-gray-300 dark:border-gray-700", 
        text: "text-gray-800 dark:text-gray-200",
        gradientText: "bg-gradient-to-r from-gray-700 to-gray-500 dark:from-gray-400 dark:to-gray-300 bg-clip-text text-transparent font-semibold"
      }; // Govt Exam Wizard - enhanced gray
      case "6": return {
        bg: "bg-gradient-to-r from-teal-200 to-teal-100 dark:from-teal-900/30 dark:to-teal-800/20", 
        border: "border-teal-300 dark:border-teal-700", 
        text: "text-teal-800 dark:text-teal-200",
        gradientText: "bg-gradient-to-r from-teal-700 to-teal-500 dark:from-teal-400 dark:to-teal-300 bg-clip-text text-transparent font-semibold"
      }; // Data Science Ace - enhanced teal
      case "7": return {
        bg: "bg-gradient-to-r from-indigo-200 to-indigo-100 dark:from-indigo-900/30 dark:to-indigo-800/20", 
        border: "border-indigo-300 dark:border-indigo-700", 
        text: "text-indigo-800 dark:text-indigo-200",
        gradientText: "bg-gradient-to-r from-indigo-700 to-indigo-500 dark:from-indigo-400 dark:to-indigo-300 bg-clip-text text-transparent font-semibold"
      }; // Coding Maestro - enhanced indigo
      case "8": return {
        bg: "bg-gradient-to-r from-red-200 to-red-100 dark:from-red-900/30 dark:to-red-800/20", 
        border: "border-red-300 dark:border-red-700", 
        text: "text-red-800 dark:text-red-200",
        gradientText: "bg-gradient-to-r from-red-700 to-red-500 dark:from-red-400 dark:to-red-300 bg-clip-text text-transparent font-semibold"
      }; // CyberSecure Pro - enhanced red
      case "9": return {
        bg: "bg-gradient-to-r from-cyan-200 to-cyan-100 dark:from-cyan-900/30 dark:to-cyan-800/20", 
        border: "border-cyan-300 dark:border-cyan-700", 
        text: "text-cyan-800 dark:text-cyan-200",
        gradientText: "bg-gradient-to-r from-cyan-700 to-cyan-500 dark:from-cyan-400 dark:to-cyan-300 bg-clip-text text-transparent font-semibold"
      }; // Cloud Architect - enhanced cyan
      case "10": return {
        bg: "bg-gradient-to-r from-orange-200 to-orange-100 dark:from-orange-900/30 dark:to-orange-800/20", 
        border: "border-orange-300 dark:border-orange-700", 
        text: "text-orange-800 dark:text-orange-200",
        gradientText: "bg-gradient-to-r from-orange-700 to-orange-500 dark:from-orange-400 dark:to-orange-300 bg-clip-text text-transparent font-semibold"
      }; // AI & ML Prodigy - enhanced orange
      default: return {
        bg: "bg-gradient-to-r from-purple-200 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/20", 
        border: "border-purple-300 dark:border-purple-700", 
        text: "text-purple-800 dark:text-purple-200",
        gradientText: "bg-gradient-to-r from-purple-700 to-purple-500 dark:from-purple-400 dark:to-purple-300 bg-clip-text text-transparent font-semibold"
      };
    }
  };
  
  const getDifficultyColor = (difficulty?: string) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'hard': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      default: return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
    }
  };

  const categoryBackgroundColor = getCategoryBackgroundColor(category.id);

  return (
    <div className="relative mb-4 sm:mb-6 last:mb-0 w-full">
      {index < totalCategories - 1 && (
        <div 
          className={`absolute left-4 sm:left-5 top-10 sm:top-12 w-0.5 sm:w-1 h-[calc(100%+0.5rem)] -z-10 bg-gradient-to-b ${
            status === "completed" 
              ? "from-[#D946EF] to-[#D946EF]"
              : status === "in-progress"
              ? "from-[#D946EF] to-gray-200 dark:to-gray-700"
              : "from-gray-200 to-gray-200 dark:from-gray-700 dark:to-gray-700"
          }`} 
        />
      )}
      
      <div className="flex flex-col gap-2 w-full">
        <Button
          variant={status === "locked" ? "outline" : "default"}
          className={`group w-full flex items-center justify-between p-2 sm:p-3 shadow-sm hover:shadow-md transition-all 
            ${categoryBackgroundColor.bg} 
            ${status !== "locked" ? `border ${categoryBackgroundColor.border}` : "border-gray-200 dark:border-gray-700"} 
            ${categoryBackgroundColor.text}
            ${status === "completed" 
              ? "bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white border-purple-600" 
              : status === "in-progress" 
              ? "bg-gradient-to-r from-purple-400 to-purple-500 hover:from-purple-500 hover:to-purple-600 text-white border-purple-500"
              : "hover:bg-opacity-90 dark:hover:bg-opacity-90"
          }`}
          onClick={() => onToggle(category.id)}
        >
          <div className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
            <div className={`flex-shrink-0 p-1 sm:p-1.5 rounded-full ${
              status === "completed" || status === "in-progress"
                ? "bg-white/20" 
                : `${categoryBackgroundColor.bg} bg-opacity-50 dark:bg-opacity-50`
            }`}>
              {getCategoryIcon(category.id)}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className={`text-xs sm:text-sm font-semibold truncate ${
                (status !== "completed" && status !== "in-progress") ? categoryBackgroundColor.gradientText : ""
              }`}>
                {category.title}
              </h3>
              <p className={`text-[10px] sm:text-xs opacity-90 ${isSmallScreen ? 'hidden' : 'truncate'}`}>
                {category.description}
              </p>
            </div>
          </div>
          <div className={`flex ${isSmallScreen ? 'flex-col items-end gap-1' : 'items-center gap-1 sm:gap-2'} ml-1 sm:ml-2 flex-shrink-0`}>
            {status !== "locked" && !isSmallScreen && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 bg-white/20 rounded-full whitespace-nowrap">
                Score: {category.minScore}%
              </span>
            )}
            
            {recommendedDifficulty && status !== "locked" && (
              <Badge variant="outline" className={`text-[9px] sm:text-[10px] px-1.5 ${getDifficultyColor(recommendedDifficulty)}`}>
                {recommendedDifficulty}
              </Badge>
            )}
            
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </div>
        </Button>
      </div>

      {isExpanded && category.subcategories && (
        <div className={`ml-6 sm:ml-8 mt-2 ${isSmallScreen ? 'space-y-3' : 'space-y-2'} w-auto pr-1`}>
          {category.subcategories.map((subcategory) => (
            <SubcategoryItem
              key={subcategory.id}
              subcategory={subcategory}
              status={getTopicStatus(subcategory)}
              recommendedDifficulty={recommendedDifficulty}
              onGenerateLecture={onGenerateLecture}
              onGenerateInsights={onGenerateInsights}
            />
          ))}
        </div>
      )}
    </div>
  );
};
